import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, UserRole } from '@types/index';
import authService from '@services/authService';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  setUser: (user: User | null) => void;
  login: (user: User) => void;
  logout: () => void;
  checkAuth: () => void;
}

/**
 * Authentication state store using Zustand
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      login: (user) => {
        set({ user, isAuthenticated: true });
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          // Even if logout fails, clear local state
          console.error('Logout error:', error);
        } finally {
          set({ user: null, isAuthenticated: false });
          // Navigate to login page
          window.location.href = '/login';
        }
      },

      checkAuth: () => {
        if (authService.hasActiveSession()) {
          set({
            isAuthenticated: true,
            user: authService.getStoredUser(),
          });
          return;
        }

        // Token present but user JSON incomplete — restore from store instead of wiping
        const token = localStorage.getItem('accessToken');
        const hasToken =
          Boolean(token) && token !== 'undefined' && token !== 'null' && Boolean(String(token).trim());
        const current = get().user;
        if (hasToken && current?.id) {
          try {
            localStorage.setItem('user', JSON.stringify(current));
          } catch {
            /* ignore */
          }
          set({ isAuthenticated: true, user: current });
          return;
        }

        // No usable session
        authService.clearLocalSession();
        set({ isAuthenticated: false, user: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);


